import axios from "axios";
import { getTurnstileToken, turnstileEnabled } from "./services/turnstile";

const API_URL = process.env.VUE_APP_BASE_URL;

/**
 * Endpoints that require a Turnstile token.
 *
 * security/0.2c (Option 4c): /api/hubspot-request is publicly reachable so the
 * marketing iframe can track anonymous resource views. The token is attached
 * here rather than at the ~17 individual call sites so no call can be missed,
 * and so future HubSpot calls are covered automatically.
 */
const TURNSTILE_PROTECTED = ["hubspot-request"];

function needsTurnstile(config) {
  const url = String(config?.url || "");
  return TURNSTILE_PROTECTED.some((path) => url.includes(path));
}

/**
 * security/2.2b: Sanctum's CSRF endpoint sits at the API ROOT
 * (`/sanctum/csrf-cookie`), one level ABOVE the `/api/` prefix that
 * VUE_APP_BASE_URL points at, so it is derived from that same env var rather
 * than carried as a second setting that can drift out of sync. The leading
 * slash makes resolution root-relative:
 * `http://host:8010/api/` -> `http://host:8010/sanctum/csrf-cookie`.
 * Falls back to a relative path when the base URL is absent (unit tests), where
 * nothing issues real requests anyway.
 */
const CSRF_COOKIE_URL = (() => {
  try {
    return new URL("/sanctum/csrf-cookie", API_URL).toString();
  } catch (e) {
    return "/sanctum/csrf-cookie";
  }
})();

const XSRF_COOKIE_NAME = "XSRF-TOKEN";
const CSRF_SAFE_METHODS = ["get", "head", "options"];

/**
 * Read a cookie by exact name. Deliberately not regex-based: the value here is
 * base64 + percent-encoded ciphertext and a pattern built around a cookie name
 * is easy to get subtly wrong. Returned RAW (still percent-encoded) because the
 * only caller is a presence check — axios does its own read and decode when it
 * attaches the X-XSRF-TOKEN header.
 */
function readCookie(name) {
  const prefix = name + "=";
  const parts = String(document.cookie || "").split(";");
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (part.indexOf(prefix) === 0) {
      return part.slice(prefix.length);
    }
  }
  return null;
}

let csrfRequest = null;

/**
 * Fetch Sanctum's XSRF-TOKEN cookie, collapsing concurrent callers onto a
 * single round trip. Deliberately swallows its own failure: if the handshake
 * cannot complete, the request that triggered it still goes out and the API's
 * own 419/401 is what the caller sees, rather than a confusing second error
 * from the bootstrap call.
 */
function fetchCsrfCookie() {
  if (!csrfRequest) {
    csrfRequest = axios
      .get(CSRF_COOKIE_URL, { withCredentials: true })
      .catch(() => null)
      .finally(() => {
        csrfRequest = null;
      });
  }
  return csrfRequest;
}

const instance = axios.create({
  baseURL: API_URL,
  // security/2.2b: send the API's session + XSRF cookies on every XHR. The
  // API's cookie is Domain=.taranawireless.com (SESSION_DOMAIN) so it applies
  // to both the SPA host (resources.*) and the API host (resourcesapi.*) --
  // which also means the SPA and the Blade admin panel share one session
  // store; see AuthController::logout() on the API for why teardown there is
  // scoped to the `web` guard rather than flushing. On localhost it is a
  // host-only cookie for `localhost`; cookies ignore port, so the SPA on :8080
  // still reads what the API on :8010 set. Requires the API to advertise
  // Access-Control-Allow-Credentials: true (see security/2.2a on the API
  // repo) or the browser silently drops the cookie on the cross-origin XHR.
  withCredentials: true,
  // security/2.2b: REQUIRED, and not implied by withCredentials. From axios 1.6
  // on, the XSRF-TOKEN cookie is only mirrored into the X-XSRF-TOKEN header
  // automatically for SAME-ORIGIN requests — see the
  // `withXSRFToken === true || (withXSRFToken == null && isURLSameOrigin(...))`
  // test in axios lib/helpers/resolveConfig.js. The SPA and the API are always
  // on different origins (localhost:8080 -> localhost:8010, resources.* ->
  // resourcesapi.*), so without this opt-in the header is silently dropped and
  // VerifyCsrfToken — injected by EnsureFrontendRequestsAreStateful on every
  // stateful /api/* request — rejects every state-changing call with a 419.
  withXSRFToken: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// security/2.2b: the Passport-bearer request interceptor that used to sit
// here has been removed. Auth state now lives in the API's HttpOnly session
// cookie, set on login. (Its name is derived from the API's APP_NAME via
// config/session.php, so it is `tarana_wireless_session`, not Laravel's stock
// `laravel_session` -- worth knowing when reading devtools.) The SPA never
// sees or handles that cookie directly, so there is nothing to read from
// localStorage and no Authorization header to attach. The Turnstile
// interceptor below still runs for the
// unauthenticated /api/hubspot-request proxy — it does not depend on session
// state and is unaffected by the auth cutover.
instance.interceptors.request.use(
  async (config) => {
    // security/2.2b: Sanctum SPA mode gates every state-changing request behind
    // a session-bound CSRF token, published as the readable XSRF-TOKEN cookie
    // by GET /sanctum/csrf-cookie. Nothing else in the app calls that endpoint,
    // so without this the very first POST — /api/login — 419s and no one can
    // get in at all.
    //
    // Done lazily on the first mutation rather than at app boot, so a cold load
    // of a public page does not pay for a round trip it may never need. The
    // cookie carries the session's lifetime, so a user idle past
    // SESSION_LIFETIME transparently re-handshakes here on their next action.
    const method = String(config.method || "get").toLowerCase();
    if (!CSRF_SAFE_METHODS.includes(method) && !readCookie(XSRF_COOKIE_NAME)) {
      await fetchCsrfCookie();
    }

    // Attach a Turnstile token to the public HubSpot proxy calls. No-op unless
    // VUE_APP_TURNSTILE_SITEKEY is set at build time. If a token cannot be
    // obtained the request still goes out unchanged — the API decides whether to
    // reject it, and tracking is best-effort rather than something that should
    // block or break the page.
    if (turnstileEnabled() && needsTurnstile(config)) {
      const turnstileToken = await getTurnstileToken();
      if (turnstileToken && config.data && typeof config.data === "object") {
        config.data = { ...config.data, turnstile_token: turnstileToken };
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handle 419 (CSRF) and 401 globally
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // security/2.2b: a 419 means the CSRF token was missing, stale, or bound to
    // a session the API has since dropped. Re-handshake and replay the request
    // exactly once. Without this, a user idle past SESSION_LIFETIME gets an
    // opaque hard failure on their next action instead of either a clean retry
    // or the 401 below that sends them to /login.
    if (error.response?.status === 419 && config && !config._csrfRetry) {
      config._csrfRetry = true;
      await fetchCsrfCookie();
      return instance(config);
    }

    if (error.response?.status === 401) {
      const lastVisitedURL = localStorage.getItem("lastVisitedURL") ?? "";
      localStorage.clear();
      localStorage.setItem("lastVisitedURL", lastVisitedURL);
      // Check if the current URL has ?v=1
      const urlParams = new URLSearchParams(window.location.search);
      const vParam = urlParams.get("v");

      if (vParam === "1") {
        // If v=1 exists, redirect with verify=0
        location.href = "/login?v=1";
      } else {
        // Normal login redirect
        location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
